/**
 * Session Context
 *
 * Provides session management and socket integration for the entire app.
 * Should wrap the application and work alongside AuthContext.
 */

import React, { createContext, useContext, useEffect, useCallback, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initializeSocket,
  registerSession,
  extendSession,
  logoutSession,
  logoutAllSessions,
  disconnectSocket,
  subscribeToEvent,
  isConnected,
} from '../services/socketService';
import type { UserSession } from '../services/socketService';
import { SessionAlerts } from '../components/feature/SessionAlerts';
import baseAPIService from '../services/baseapiservice';

interface SessionContextType {
  // State
  isSocketConnected: boolean;
  sessionRegistered: boolean;
  activeSessions: UserSession[];
  // Actions
  registerUserSession: () => void;
  refreshSession: () => Promise<number | void>;
  terminateOtherSessions: () => void;
  logoutFromAllDevices: () => void;
}

const SessionContext = createContext<SessionContextType | null>(null);

interface SessionProviderProps {
  children: ReactNode;
}

// Helper to decode JWT and get expiry
function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp || null;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: SessionProviderProps) {
  const navigate = useNavigate();
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [sessionRegistered, setSessionRegistered] = useState(false);
  const [activeSessions, setActiveSessions] = useState<UserSession[]>([]);

  // Initialize socket on mount
  useEffect(() => {
    const socket = initializeSocket();

    // Set initial state
    setIsSocketConnected(isConnected());

    // Subscribe to connection events
    const unsubConnect = subscribeToEvent('connect', () => {
      setIsSocketConnected(true);
      // Auto-register session if user is logged in
      autoRegisterSession();
    });

    const unsubDisconnect = subscribeToEvent('disconnect', () => {
      setIsSocketConnected(false);
      setSessionRegistered(false);
    });

    const unsubRegistered = subscribeToEvent('session:registered', () => {
      setSessionRegistered(true);
    });

    // Cleanup
    return () => {
      unsubConnect();
      unsubDisconnect();
      unsubRegistered();
    };
  }, []);

  // Auto-register session if user is already logged in
  const autoRegisterSession = useCallback(() => {
    const token = localStorage.getItem('x-access-token');
    const userDataStr = localStorage.getItem('user_data');

    if (token && userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        const tokenExpiry = getTokenExpiry(token);

        if (tokenExpiry && userData.id && userData.username) {
          registerSession({
            userId: userData.id.toString(),
            username: userData.username || userData.email || 'User',
            tokenExpiry
          });
        }
      } catch (error) {
        console.error('Failed to auto-register session:', error);
      }
    }
  }, []);

  // Register user session manually
  const registerUserSession = useCallback(() => {
    autoRegisterSession();
  }, [autoRegisterSession]);

  // Refresh session (extend token)
  const refreshSession = useCallback(async (): Promise<number | void> => {
    const token = localStorage.getItem('x-access-token');
    const authType = localStorage.getItem('auth_type') || 'erp';

    if (!token) {
      console.warn('No token to refresh');
      return;
    }

    try {
      // Call the appropriate verify/refresh endpoint
      // Use apufidc-auth for APUFIDC users (verifies against apufidc_enc_db schema)
      const endpoint = authType === 'crda'
        ? 'crda-auth/verify'
        : authType === 'apufidc'
          ? 'apufidc-auth/verify'
          : 'erp-auth/verify';

      const response = await baseAPIService.post(endpoint, {});

      if (response.status >= 200 && response.status < 300) {
        const data = response.data as { token?: string };

        // If a new token is provided, update it
        if (data.token) {
          localStorage.setItem('x-access-token', data.token);
          const newExpiry = getTokenExpiry(data.token);

          if (newExpiry) {
            extendSession(newExpiry);
            return newExpiry;
          }
        } else {
          // Token is still valid, just extend the session monitoring
          const currentExpiry = getTokenExpiry(token);
          if (currentExpiry) {
            // Add 30 minutes to current time as a fallback
            const extendedExpiry = Math.floor(Date.now() / 1000) + (30 * 60);
            extendSession(extendedExpiry);
            return extendedExpiry;
          }
        }
      } else {
        console.error('Failed to refresh token:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
    }
  }, []);

  // Terminate other sessions
  const terminateOtherSessions = useCallback(() => {
    // This would call the backend to terminate other sessions
    // For now, we'll just logout from all devices except current
    logoutAllSessions();
  }, []);

  // Logout from all devices
  const logoutFromAllDevices = useCallback(() => {
    logoutAllSessions();
    handleLogout();
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    // Clear local storage
    localStorage.removeItem('x-access-token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_type');
    localStorage.removeItem('landing_page_url');

    // Disconnect socket
    logoutSession();
    disconnectSocket();

    // Reset state
    setSessionRegistered(false);
    setActiveSessions([]);

    // Navigate to login
    navigate('/login');
  }, [navigate]);

  const contextValue: SessionContextType = {
    isSocketConnected,
    sessionRegistered,
    activeSessions,
    registerUserSession,
    refreshSession,
    terminateOtherSessions,
    logoutFromAllDevices
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
      <SessionAlerts
        onLogout={handleLogout}
        onExtendSession={refreshSession}
      />
    </SessionContext.Provider>
  );
}

/**
 * Hook to access session context
 */
export function useSession(): SessionContextType {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

export default SessionContext;
